import { AdditiveBlending, Color, Mesh, PlaneGeometry, ShaderMaterial } from "three";
import type { AtmosphereUniforms } from "../atmosphere.ts";

const vertexShader = /* glsl */ `
out vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const fragmentShader = /* glsl */ `
uniform vec3 uColor;
uniform float uNotches;
// 1 no canvas; > 1 com o bloom do compositor (a atmosfera, F52).
uniform float uGlowGain;
in vec2 vUv;
void main() {
  vec2 p = vUv * 2.0 - 1.0;
  float r = length(p);
  float aa = fwidth(r) * 1.5;
  // Anel fino e, por fora dele, N marcas lado a lado à frente do corpo (o número do slot sem cor).
  float ring = smoothstep(0.74 - aa, 0.74, r) * (1.0 - smoothstep(0.84, 0.84 + aa, r));
  float a = atan(p.x, p.y);
  float marks = 0.0;
  for (int i = 0; i < 4; i++) {
    if (float(i) >= uNotches) break;
    float c = (float(i) - (uNotches - 1.0) * 0.5) * 0.3;
    float da = abs(a - c);
    marks = max(marks, (1.0 - smoothstep(0.07, 0.07 + aa * 2.0, da)) * smoothstep(0.88 - aa, 0.88, r) * (1.0 - smoothstep(0.98, 0.98 + aa, r)));
  }
  // Fase 13 ("Mintlit"): a faixa em HDR (o bloom a pega) com um halo suave no chão em volta.
  float halo = exp(-abs(r - 0.79) * 11.0) * 0.22 * (1.0 - smoothstep(0.9, 1.0, r));
  float alpha = max(max(ring, marks), halo);
  if (alpha <= 0.002) discard;
  vec3 c = uColor * (ring * 3.2 + marks * 1.6 + halo);
  // Sem bloom (o canvas corta cada canal em 1), o HDR viraria outro matiz (o menta puxava ao ciano):
  // limita pelo maior canal; com bloom, a correção de cor do pós faz o mesmo depois do halo.
  if (uGlowGain <= 1.0) c /= max(1.0, max(c.r, max(c.g, c.b)));
  gl_FragColor = vec4(c, 1.0);
  #include <colorspace_fragment>
}
`;

/** Um material por cor de slot e número de marcas (dividido por quem tem o mesmo slot). */
const cache = new WeakMap<AtmosphereUniforms, Map<string, ShaderMaterial>>();
const geometry = new PlaneGeometry(1.5, 1.5).rotateX(-Math.PI / 2);

/**
 * Anel sob os pés do jogador (F63, doc 09 §2.1): a cor do slot e 1 a 4 marcas à frente (o número do
 * slot sem depender de cor, para o daltonismo).
 */
export function createSlotRing(atmosphere: AtmosphereUniforms, hex: string, notches: number): Mesh {
  let materials = cache.get(atmosphere);
  if (!materials) cache.set(atmosphere, (materials = new Map()));
  const key = `${hex}:${notches}`;
  let material = materials.get(key);
  if (!material) {
    material = new ShaderMaterial({
      name: "anel-do-slot",
      uniforms: { uColor: { value: new Color(hex) }, uNotches: { value: Math.max(1, Math.min(4, notches)) }, uGlowGain: atmosphere.uGlowGain },
      vertexShader,
      fragmentShader,
      transparent: true,
      depthWrite: false,
      blending: AdditiveBlending,
      polygonOffset: true,
      polygonOffsetFactor: -2,
      polygonOffsetUnits: -4,
    });
    materials.set(key, material);
  }
  const mesh = new Mesh(geometry, material);
  mesh.name = "anel-do-slot";
  mesh.position.y = 0.04;
  mesh.renderOrder = 4;
  return mesh;
}
